import React, { useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import Modal from '../../Modal';
import Alert from '../../Alert';
import RegisteredIcon from '../../RegisteredIcon';
import './LinktreeImport.css';

const STEP_HANDLE = 'handle';
const STEP_PREVIEW = 'preview';
const STEP_RESULT = 'result';

// Maps the typed error codes documented in docs/STOREFRONT-MANAGER.md's API reference to
// seller-facing copy. Codes we don't recognise (or a bare network failure) fall back to a
// generic message rather than surfacing SSRF/internal details.
const ERROR_MESSAGES = {
    BAD_HANDLE: 'That doesn’t look like a Linktree username or URL. Try just your '
        + 'username, or a link like linktr.ee/yourname.',
    LINKTREE_UNREADABLE: 'We couldn’t find anything on that Linktree page. Double-check '
        + 'the username, or add your socials and links manually.',
    FETCH_BLOCKED: 'We couldn’t reach that Linktree page right now. Please try again in a moment.',
    FETCH_FAILED: 'We couldn’t reach that Linktree page right now. Please try again in a moment.',
    RATE_LIMITED: 'You’ve tried this a few times recently. Please wait a few minutes and try again.',
    INVALID_SELECTION: 'That selection wasn’t valid. Please try again.',
    SELECTION_TOO_LARGE: 'That’s too many rows to import at once. Try importing fewer at a time.',
};

function friendlyError(code) {
    return ERROR_MESSAGES[code] || 'Something went wrong. Please try again.';
}

export default function LinktreeImport({
    onImported,
    triggerLabel = 'Import from Linktree',
    triggerClassName = 'button secondary',
}) {
    const { fetch, API_URL } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState(STEP_HANDLE);
    const [handle, setHandle] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [warnings, setWarnings] = useState([]);
    const [socialRows, setSocialRows] = useState([]);
    const [linkRows, setLinkRows] = useState([]);
    const [result, setResult] = useState(null);

    function resetState() {
        setStep(STEP_HANDLE);
        setHandle('');
        setLoading(false);
        setError('');
        setWarnings([]);
        setSocialRows([]);
        setLinkRows([]);
        setResult(null);
    }

    function closeModal() {
        setIsOpen(false);
        resetState();
    }

    async function handlePreviewSubmit(event) {
        event.preventDefault();
        const trimmedHandle = handle.trim();
        if (!trimmedHandle) {
            setError('Enter your Linktree username or URL.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/v1/seller/linktree-import/preview`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ handle: trimmedHandle }),
            });
            const body = await response.json().catch(() => ({}));

            if (!response.ok) {
                setError(friendlyError(body.code));
                return;
            }

            const data = body.data || {};
            setSocialRows((data.socials || []).map(social => ({ ...social, selected: true, replace: false })));
            setLinkRows((data.links || []).map(link => ({ ...link, selected: true })));
            setWarnings(data.warnings || []);
            setStep(STEP_PREVIEW);
        } catch (err) {
            console.error('Error previewing Linktree import:', err);
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    function toggleSocialSelected(index) {
        setSocialRows(rows => rows.map((row, i) => (i === index ? { ...row, selected: !row.selected } : row)));
    }

    function toggleSocialReplace(index) {
        setSocialRows(rows => rows.map((row, i) => (i === index ? { ...row, replace: !row.replace } : row)));
    }

    function toggleLinkSelected(index) {
        setLinkRows(rows => rows.map((row, i) => (i === index ? { ...row, selected: !row.selected } : row)));
    }

    const selectedSocialCount = socialRows.filter(row => row.selected).length;
    const selectedLinkCount = linkRows.filter(row => row.selected).length;
    const selectedCount = selectedSocialCount + selectedLinkCount;

    async function handleApply() {
        if (selectedCount === 0) {
            setError('Select at least one row to import.');
            return;
        }

        setLoading(true);
        setError('');

        const payload = {
            socials: socialRows
                .filter(row => row.selected)
                .map(row => ({
                    platformKey: row.platformKey,
                    url: row.url,
                    replace: row.alreadySet ? Boolean(row.replace) : false,
                })),
            links: linkRows
                .filter(row => row.selected)
                .map(row => ({ title: row.title, url: row.url, icon: row.icon })),
        };

        try {
            const response = await fetch(`${API_URL}/v1/seller/linktree-import/apply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const body = await response.json().catch(() => ({}));

            if (!response.ok) {
                setError(friendlyError(body.code));
                return;
            }

            setResult(body.data || {});
            setStep(STEP_RESULT);
            onImported?.();
        } catch (err) {
            console.error('Error applying Linktree import:', err);
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    function handleImportAnother() {
        resetState();
    }

    return (
        <div className="lti-root">
            <button type="button" className={triggerClassName} onClick={() => setIsOpen(true)}>
                <i className="fa-solid fa-file-import" aria-hidden="true"></i> {triggerLabel}
            </button>

            <Modal
                isOpen={isOpen}
                onClose={closeModal}
                title="Import from Linktree"
                size="large"
                className="lti-modal"
            >
                {step === STEP_HANDLE && (
                    <form onSubmit={handlePreviewSubmit} className="lti-handle-form">
                        <p>
                            Paste your Linktree URL or username. We&rsquo;ll show you a preview of the
                            socials and links we find. Nothing is saved until you confirm.
                        </p>
                        <div className="group">
                            <label htmlFor="lti-handle">Linktree username or URL</label>
                            <input
                                id="lti-handle"
                                type="text"
                                placeholder="linktr.ee/yourname"
                                value={handle}
                                onChange={event => setHandle(event.target.value)}
                                disabled={loading}
                                autoFocus
                            />
                        </div>
                        {error && <Alert type="error">{error}</Alert>}
                        <div className="group lti-actions">
                            <button type="submit" className="button primary" disabled={loading}>
                                {loading ? 'Looking…' : 'Preview'}
                            </button>
                            <button type="button" className="button secondary" onClick={closeModal} disabled={loading}>
                                Cancel
                            </button>
                        </div>
                    </form>
                )}

                {step === STEP_PREVIEW && (
                    <div className="lti-preview">
                        {warnings.length > 0 && (
                            <Alert type="warning">
                                {warnings.length} row{warnings.length === 1 ? '' : 's'} skipped: {warnings.join(' ')}
                            </Alert>
                        )}
                        {error && <Alert type="error">{error}</Alert>}

                        {socialRows.length === 0 && linkRows.length === 0 ? (
                            <p>We didn&rsquo;t find anything importable on that page.</p>
                        ) : (
                            <>
                                {socialRows.length > 0 && (
                                    <fieldset className="group lti-section">
                                        <legend>Social links ({socialRows.length})</legend>
                                        <ul className="lti-rows">
                                            {socialRows.map((row, index) => {
                                                const rowId = `lti-social-${index}`;
                                                return (
                                                    <li key={rowId} className="lti-row">
                                                        <label className="checkbox" htmlFor={rowId}>
                                                            <input
                                                                id={rowId}
                                                                type="checkbox"
                                                                checked={row.selected}
                                                                onChange={() => toggleSocialSelected(index)}
                                                            />
                                                            <span>
                                                                <RegisteredIcon value={row.icon} /> {row.label}
                                                                <span className="lti-row-url" title={row.url}>{row.url}</span>
                                                            </span>
                                                        </label>
                                                        {row.alreadySet && (
                                                            <label className="checkbox lti-replace" htmlFor={`${rowId}-replace`}>
                                                                <input
                                                                    id={`${rowId}-replace`}
                                                                    type="checkbox"
                                                                    checked={row.replace}
                                                                    disabled={!row.selected}
                                                                    onChange={() => toggleSocialReplace(index)}
                                                                />
                                                                <span>Replace your existing {row.label} link</span>
                                                            </label>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </fieldset>
                                )}

                                {linkRows.length > 0 && (
                                    <fieldset className="group lti-section">
                                        <legend>Links ({linkRows.length})</legend>
                                        <ul className="lti-rows">
                                            {linkRows.map((row, index) => {
                                                const rowId = `lti-link-${index}`;
                                                return (
                                                    <li key={rowId} className="lti-row">
                                                        <label className="checkbox" htmlFor={rowId}>
                                                            <input
                                                                id={rowId}
                                                                type="checkbox"
                                                                checked={row.selected}
                                                                onChange={() => toggleLinkSelected(index)}
                                                            />
                                                            <span>
                                                                {row.icon && <RegisteredIcon value={row.icon} />} {row.title}
                                                                <span className="lti-row-url" title={row.url}>{row.url}</span>
                                                            </span>
                                                        </label>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </fieldset>
                                )}
                            </>
                        )}

                        <div className="group lti-actions">
                            <button
                                type="button"
                                className="button primary"
                                onClick={handleApply}
                                disabled={loading || selectedCount === 0}
                            >
                                {loading ? 'Importing…' : `Import selected (${selectedCount})`}
                            </button>
                            <button type="button" className="button secondary" onClick={closeModal} disabled={loading}>
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {step === STEP_RESULT && result && (
                    <div className="lti-result">
                        <Alert type="success">Import complete!</Alert>
                        <ul>
                            {result.socialsAdded > 0 && (
                                <li>{result.socialsAdded} social link{result.socialsAdded === 1 ? '' : 's'} added</li>
                            )}
                            {result.socialsReplaced > 0 && (
                                <li>{result.socialsReplaced} social link{result.socialsReplaced === 1 ? '' : 's'} replaced</li>
                            )}
                            {result.linksAdded > 0 && (
                                <li>{result.linksAdded} link{result.linksAdded === 1 ? '' : 's'} added</li>
                            )}
                            {result.skipped > 0 && (
                                <li>{result.skipped} row{result.skipped === 1 ? '' : 's'} skipped</li>
                            )}
                            {!result.socialsAdded && !result.socialsReplaced && !result.linksAdded && (
                                <li>Nothing new was added.</li>
                            )}
                        </ul>
                        <div className="group lti-actions">
                            <button type="button" className="button secondary" onClick={handleImportAnother}>
                                Import another
                            </button>
                            <button type="button" className="button primary" onClick={closeModal}>
                                Done
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
